import { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScenarioCreateInput, ScenarioPlanFragmentFragment, ScenarioPlanName } from '../../api/gql';
import { FormInput } from '../../components/form/FormInput';
import { FormSelect } from '../../components/form/FormSelect';
import { Tabs } from '../../components/tabs';
import { scenarioPlanToTransMap } from '../../utils/maps';
import { scenarioCreateValidationSchema } from '../../utils/validationSchemas';
import { ModelFieldsOverview } from './components/ModelFieldsOverview';
import { ModelsFormAutocomplete } from './components/ModelsFormAutocomplete';
import { UsersFormAutocomplete } from './components/UsersFormAutocomplete';

const TRANS_NS = 'universal-reminding';

type ScenarioFormProps =
  | {
      readonly: true;
      scenarioPlans: ScenarioPlanFragmentFragment[];
      onSubmit?: never;
      isLoading?: never;
      isDisabled?: never;
      defaultValues?: ScenarioCreateInput;
    }
  | {
      readonly?: false;
      scenarioPlans: ScenarioPlanFragmentFragment[];
      onSubmit: (data: ScenarioCreateInput) => void;
      isLoading?: boolean;
      isDisabled?: boolean;
      defaultValues?: ScenarioCreateInput;
    };

const padOrTrim = (array: string[] | undefined, length: number, fill: string) =>
  Array.from({ length }, (_, i) => array?.[i] ?? fill);

export const ScenarioForm = ({
  readonly,
  scenarioPlans,
  onSubmit,
  isLoading,
  isDisabled,
  defaultValues,
}: ScenarioFormProps) => {
  const { t } = useTranslation(TRANS_NS, { keyPrefix: 'scenarios.form' });

  const defaultPlan = scenarioPlans.find((plan) => plan.name === ScenarioPlanName.Plan_551);
  const { control, handleSubmit, reset, getValues, setValue } = useForm<ScenarioCreateInput>({
    resolver: yupResolver(scenarioCreateValidationSchema),
    defaultValues: {
      subject: Array.from({ length: defaultPlan?.email ?? 0 }, () => t('subjectTemplate') as string),
      textEmail: Array.from({ length: defaultPlan?.email ?? 0 }, () => t('emailTemplate') as string),
      textSms: Array.from({ length: defaultPlan?.sms ?? 0 }, () => t('smsTemplate') as string),
      textCall: Array.from({ length: defaultPlan?.call ?? 0 }, () => t('callTemplate') as string),
      plan: defaultPlan?.name,
      ...defaultValues,
    },
  });

  const selectedPlanName = useWatch({ control, name: 'plan' });

  const selectedScenarioPlan = useMemo(
    () => scenarioPlans.find((plan) => plan.name === selectedPlanName),
    [selectedPlanName, scenarioPlans],
  );

  useEffect(() => {
    if (readonly || !selectedScenarioPlan) return;

    const current = getValues();

    setValue('subject', padOrTrim(current.subject, selectedScenarioPlan.email ?? 0, t('subjectTemplate') as string));
    setValue('textEmail', padOrTrim(current.textEmail, selectedScenarioPlan.email ?? 0, t('emailTemplate') as string));
    setValue('textSms', padOrTrim(current.textSms, selectedScenarioPlan.sms ?? 0, t('smsTemplate') as string));
    setValue('textCall', padOrTrim(current.textCall, selectedScenarioPlan.call ?? 0, t('callTemplate') as string));
  }, [selectedScenarioPlan, readonly, getValues, setValue, t]);

  // handle values change in readonly mode
  useEffect(() => {
    if (readonly && defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, readonly]);

  const selectedModelId = useWatch({ control, name: 'modelId' });

  const texts = useWatch({ control, name: ['textEmail', 'textSms'] });

  const usedFields = useMemo(() => {
    const result: { email: string[][]; sms: string[][] } = {
      email: Array.from({ length: selectedScenarioPlan?.email ?? 0 }, () => []),
      sms: Array.from({ length: selectedScenarioPlan?.sms ?? 0 }, () => []),
    };

    (texts ?? []).forEach((textCollection, textCollectionIndex) => {
      (textCollection ?? []).forEach((text, index) => {
        const variables = text?.match(/\[([^\]]+)]/g) ?? [];
        const key = textCollectionIndex === 0 ? 'email' : 'sms';
        result[key][index] = variables;
      });
    });

    return result;
  }, [texts, selectedScenarioPlan]);

  return (
    <form onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        <Box flex={1} display={'flex'} flexDirection={'column'} gap={2}>
          <FormInput control={control} name={'name'} label={t('name') + '*'} readonly={readonly} />
          <UsersFormAutocomplete control={control} name={'userId'} label={t('user') + '*'} readonly={readonly} />
          <ModelsFormAutocomplete control={control} name={'modelId'} label={t('model') + '*'} readonly={readonly} />
          <FormSelect
            control={control}
            name={'plan'}
            label={t('plan') + '*'}
            readonly={readonly}
            size={'medium'}
            options={scenarioPlans.map((plan) => ({
              value: plan.name,
              label: t(scenarioPlanToTransMap[plan.name], {
                ns: TRANS_NS,
                keyPrefix: 'scenarios',
              }),
            }))}
          />
          {readonly ? null : (
            <ModelFieldsOverview
              id={selectedModelId}
              usedFields={usedFields}
              sx={{ flex: 1, bgcolor: 'surface.light' }}
            />
          )}
        </Box>
        <Box flex={2} display={'flex'} flexDirection={'column'} gap={4}>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Box>
              <Typography variant={'caption'} color={'text.secondary'}>
                {t('email')}
              </Typography>
              <Divider />
            </Box>
            <Tabs
              items={Array.from({ length: selectedScenarioPlan?.email ?? 0 }, (_, index) => (
                <Stack gap={2} key={index}>
                  <FormInput
                    name={`subject.${index}`}
                    control={control}
                    readonly={readonly}
                    label={t('subject') + '*'}
                  />
                  <FormInput
                    name={`textEmail.${index}`}
                    control={control}
                    readonly={readonly}
                    multiline={true}
                    rows={11}
                    sx={{ whiteSpace: 'break-spaces' }}
                    label={t('emailBody') + '*'}
                  />
                </Stack>
              ))}
              getTabName={(index) => `${index + 1}. ${t('email')}`}
            />
          </Box>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Box>
              <Typography variant={'caption'} color={'text.secondary'}>
                {t('sms')}
              </Typography>
              <Divider />
            </Box>
            <Tabs
              items={Array.from({ length: selectedScenarioPlan?.sms ?? 0 }, (_, index) => (
                <FormInput
                  key={index}
                  name={`textSms.${index}`}
                  control={control}
                  readonly={readonly}
                  multiline={true}
                  rows={3}
                  label={t('smsBody') + '*'}
                />
              ))}
              getTabName={(index) => `${index + 1}. ${t('sms')}`}
            />
          </Box>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Box>
              <Typography variant={'caption'} color={'text.secondary'}>
                {t('call')}
              </Typography>
              <Divider />
            </Box>
            <Tabs
              items={Array.from({ length: selectedScenarioPlan?.call ?? 0 }, (_, index) => (
                <FormInput
                  key={index}
                  name={`textCall.${index}`}
                  control={control}
                  readonly={readonly}
                  multiline={true}
                  rows={3}
                  label={t('callBody') + '*'}
                  helperText={readonly ? undefined : t('callHelperText')}
                />
              ))}
              getTabName={(index) => `${index + 1}. ${t('call')}`}
            />
          </Box>
        </Box>
        {onSubmit ? (
          <Box width={'100%'} display={'flex'} justifyContent={'center'}>
            <LoadingButton
              variant={'contained'}
              type={'submit'}
              size={'large'}
              loading={isLoading}
              disabled={isDisabled}
            >
              {t('submit')}
            </LoadingButton>
          </Box>
        ) : null}
      </Stack>
    </form>
  );
};
